// Shared constants and pure helpers for the restoration calculators.
// No React, no "use client" — safe to import from any context.

export type DehuModel = { id: string; label: string; aham: number; amps: number };

export const DEHU_CLASS_FACTOR: Record<string, number> = {
  "1": 0.1,
  "2": 0.2,
  "3": 0.3,
  "4": 0.4,
};

export const DEHU_CATALOG: DehuModel[] = [
  { id: "alorair-sentinel-hd55",      label: "Alorair Sentinel HD55",           aham: 55,  amps: 4.2 },
  { id: "alorair-sentinel-hdi90",     label: "Alorair Sentinel HDi90",          aham: 90,  amps: 6.5 },
  { id: "alorair-sentinel-hdi100",    label: "Alorair Sentinel HDi100",         aham: 100, amps: 7.0 },
  { id: "alorair-storm-lgr-extreme",  label: "Alorair Storm LGR Extreme",       aham: 125, amps: 8.0 },
  { id: "alorair-storm-pro",          label: "Alorair Storm Pro",               aham: 180, amps: 8.5 },
  { id: "arid-dry-ms-3600",           label: "Arid Dry MS-3600",                aham: 66,  amps: 5.0 },
  { id: "arid-dry-ms-4800",           label: "Arid Dry MS-4800",                aham: 88,  amps: 6.0 },
  { id: "arid-dry-ms-6000",           label: "Arid Dry MS-6000",                aham: 110, amps: 7.5 },
  { id: "arid-dry-ms-8500",           label: "Arid Dry MS-8500",                aham: 155, amps: 11.0 },
  { id: "arid-dry-ms-14500",          label: "Arid Dry MS-14500",               aham: 265, amps: 15.0 },
  { id: "arid-dry-ms-17500",          label: "Arid Dry MS-17500",               aham: 320, amps: 18.0 },
  { id: "arid-dry-ms-27500",          label: "Arid Dry MS-27500",               aham: 500, amps: 24.0 },
  { id: "bair-vantage-vg-1500",       label: "B-Air Vantage VG-1500",           aham: 80,  amps: 5.5 },
  { id: "bair-vantage-2200",          label: "B-Air Vantage 2200",              aham: 120, amps: 7.2 },
  { id: "bair-vantage-3000",          label: "B-Air Vantage 3000",              aham: 155, amps: 8.5 },
  { id: "capture-65l-230v",           label: "Capture 65L (230V)",              aham: 137, amps: 4.5 },
  { id: "century-400-crusader-75",    label: "Century 400 Crusader 75",         aham: 75,  amps: 5.0 },
  { id: "century-400-crusader-95",    label: "Century 400 Crusader 95",         aham: 95,  amps: 6.2 },
  { id: "cti-drymax-2000",            label: "CTI DryMax 2000",                 aham: 110, amps: 7.0 },
  { id: "dri-eaz-110",                label: "Dri-Eaz 110",                     aham: 72,  amps: 5.4 },
  { id: "dri-eaz-1200",               label: "Dri-Eaz 1200",                    aham: 103, amps: 8.3 },
  { id: "dri-eaz-2000",               label: "Dri-Eaz 2000",                    aham: 98,  amps: 8.0 },
  { id: "dri-eaz-2400",               label: "Dri-Eaz 2400",                    aham: 145, amps: 8.5 },
  { id: "dri-eaz-2800i",              label: "Dri-Eaz 2800i",                   aham: 165, amps: 9.0 },
  { id: "dri-eaz-3500i",              label: "Dri-Eaz 3500i",                   aham: 175, amps: 9.5 },
  { id: "dri-eaz-6000li",             label: "Dri-Eaz 6000Li",                  aham: 115, amps: 6.3 },
  { id: "dri-eaz-f413",               label: "Dri-Eaz F413 Revolution",         aham: 120, amps: 6.0 },
  { id: "dri-eaz-lgr-vault",          label: "Dri-Eaz LGR Vault",               aham: 130, amps: 8.5 },
  { id: "dri-eaz-lgr-7000xli",        label: "Dri-Eaz LGR 7000XLi",             aham: 175, amps: 9.5 },
  { id: "dri-eaz-phd-200",            label: "Dri-Eaz PHD 200",                 aham: 170, amps: 8.4 },
  { id: "phoenix-r150",               label: "Phoenix R150 LGR",                aham: 95,  amps: 6.5 },
  { id: "phoenix-r200",               label: "Phoenix R200 LGR",                aham: 147, amps: 8.2 },
  { id: "phoenix-200-max",            label: "Phoenix 200 Max LGR",             aham: 198, amps: 8.6 },
  { id: "phoenix-300-max",            label: "Phoenix 300 Max LGR",             aham: 240, amps: 12.0 },
  { id: "phoenix-r250-lgr",           label: "Phoenix R250 LGR",                aham: 158, amps: 7.6 },
  { id: "std-70-pint",                label: "Standard 70-pint (residential)",  aham: 70,  amps: 6.5 },
];

export const NAM_UNITS: { value: string; label: string }[] = [
  { value: "500",  label: "Dri-Eaz DefendAir H500 — 500 CFM" },
  { value: "750",  label: "B-Air HEPA-750 — 750 CFM" },
  { value: "1000", label: "Dri-Eaz DefendAir H1000 — 1000 CFM" },
  { value: "1200", label: "Global Plasma GP2400 — 1200 CFM" },
  { value: "1500", label: "Pullman Holt HEPA — 1500 CFM" },
  { value: "2000", label: "Industrial HEPA H2000 — 2000 CFM" },
];

export function toNum(v: string | number): number {
  const n = parseFloat(String(v));
  return Number.isFinite(n) && n > 0 ? n : 0;
}

export function fmt(n: number, digits = 0): string {
  if (!Number.isFinite(n)) return "—";
  return n.toLocaleString(undefined, {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });
}
